import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import classNames from 'classnames';
import Loader from '@/components/common/Loader';
import DataList from '@/modules/DataList';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import { useEntityDetails } from '@/modules/EntityDetails';
import { useQueryProvider } from '@/modules/QueryProvider';
import GroupingType from '../constants/GroupingType';
import OpportunitiesSortableFields from '../constants/OpportunitiesSortableFields';
import useGroupedOpportunitiesTotals from '../hooks/useGroupedOpportunitiesTotals';
import useOpportunitiesDataListColumns from '../hooks/useOpportunitiesDataListColumns';
import useOpportunitiesDataListData from '../hooks/useOpportunitiesDataListData';
import type OpportunitiesDataListRow from '../types/OpportunitiesDataListRow';
import type OpportunitiesFilterParams from '../types/OpportunitiesFilterParams';
import GroupedStageTotals from './GroupedStageTotals';

export default function GroupedOpportunitiesDataList({
  header,
  id,
  type,
}: {
  header: string;
  id: string;
  type: GroupingType;
}) {
  const { filters } = useQueryProvider<OpportunitiesFilterParams>();
  const [isOpen, setIsOpen] = React.useState(false);
  const { openOpportunityEntity } = useEntityDetails();
  const {
    isLagging,
    isLoading: isLoadingDataList,
    opportunities,
    totalHits,
  } = useOpportunitiesDataListData({
    ...(type === GroupingType.CONFIDENCE_LEVEL && { confidenceLevelId: id }),
    ...(type === GroupingType.STAGE && { opportunityStageId: id }),
  });
  const { data: totalsData, isLoading: isLoadingTotals } =
    useGroupedOpportunitiesTotals({
      id,
      type,
      filters,
    });

  const handleRowClick = React.useCallback((row: OpportunitiesDataListRow) => {
    openOpportunityEntity(row.opportunityId);
  }, []);
  const { query } = useQueryProvider();

  const isShowAllOpportunities = query.archived !== 'all';

  const columns = useOpportunitiesDataListColumns({
    shouldRemoveOnArchive: isShowAllOpportunities,
    shouldRemoveOnUnarchive: isShowAllOpportunities,
  });

  const handleToggleClick = () => setIsOpen((prev) => !prev);

  if (isLoadingDataList || isLoadingTotals) {
    return <Loader isCentered />;
  }

  if (!totalHits) {
    return null;
  }

  return (
    <Accordion
      className="overflow-hidden rounded-lg border border-grey-300 shadow-none before:h-0"
      expanded={isOpen}
    >
      <AccordionSummary
        classes={{
          root: 'bg-grey-100',
          content:
            'order-2 justify-between @container/AccordionSummary gap-4 items-center',
          expandIconWrapper: 'mr-4',
        }}
        expandIcon={<KeyboardArrowDownIcon />}
        onClick={handleToggleClick}
      >
        <h3 className="grow type-h2">
          {header} ({totalHits})
        </h3>
        {totalsData && <GroupedStageTotals {...totalsData} />}
      </AccordionSummary>

      <AccordionDetails
        classes={{
          root: 'px-0 pt-0 [&>:last-child]:mx-4',
        }}
      >
        <DataList<OpportunitiesDataListRow>
          TableRowProps={({ archivedAt }) => ({
            onClick: handleRowClick,
            className: classNames('cursor-pointer p-2 md:p-0', {
              'italic [&_td]:text-text-disabled [&_td]:hover:text-text-secondary':
                archivedAt,
            }),
            hover: true,
          })}
          columns={columns}
          data={opportunities}
          isLagging={isLagging}
          rowKey="opportunityId"
          sortableFields={OpportunitiesSortableFields}
          totalRows={totalHits}
        />
      </AccordionDetails>
    </Accordion>
  );
}
